import EventBus from '@/event-bus';
import Shop from '@/shop';
import cloneDeep from 'lodash/cloneDeep';
import _isArray from 'lodash/isArray';
import haveSameKeys from '@/helpers/haveSameKeys';

class DataLayer {
  constructor(dataLayerName = 'gtmDataLayer') {
    if (Shop.config.config.theme_generation > 2) {
      this.init(dataLayerName);

      return;
    }

    // Во втором поколении в наших старых шаблонах CommonJS инитится раньше чем скрипт электронной коммерции и создаёт свой dataLayer.
    // После CommonJS инитится скрипт электронной коммерции и перезатирает созданный нами dataLayer пустым массивом,
    // но мы в коммоне об этом ничего не знаем, так как сохранили ссылку на объект (this.layer).
    //
    // Однако в современных шаблонах второго поколения скрипт может подключаться в конце body, поэтому DOM уже готов к моменту выполнения.
    // DOMContentLoaded возможно уже сработал, поэтому проверяем readyState и инициализируем сразу если DOM уже готов.
    const isDOMReady = window.document.readyState === 'complete' || window.document.readyState === 'interactive';

    if (isDOMReady) {
      this.init(dataLayerName);
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        this.init(dataLayerName);
      });
    }
  }

  init(dataLayerName) {
    this.config = Shop.config.get([ 'currency_iso_code' ]);
    this.counterSettings = Shop.config.get('counter_settings').counter_settings;

    this.ecommerce = { ecommerce: { currencyCode: this.config.currency_iso_code } };
    this.savedOrderLines = [];

    // Используем переданное имя контейнера
    window[dataLayerName] = window[dataLayerName] || [];
    this.layer = window[dataLayerName];

    this.sberAdsDataLayerName = this.counterSettings.sber_ads_data_layer_name;

    if (this.sberAdsDataLayerName) {
      window[this.sberAdsDataLayerName] = window[this.sberAdsDataLayerName] || [];
    }

    if (this.counterSettings.add_to_cart_event) {
      this.addProductEvent();
    }
    if (this.counterSettings.remove_from_cart_event) {
      this.deleteProductEvent();
    }
    if (this.counterSettings.add_to_wishlist_event) {
      this.addToFavoritesProductsEvent();
    }
    this.changeNumberOfProductInCartEvent();
    this.quickCheckoutSuccessEvent();
  }

  addProductEvent() {
    EventBus.subscribe('add_items:insales:cart', (eventData) => {
      this.send(this.getAddedProducts(eventData), 'add_to_cart');
    });

    EventBus.subscribe('add_checkout:insales:cart', (eventData) => {
      this.send(this.getAddedProducts(eventData), 'add_to_cart');
    });
  }

  deleteProductEvent() {
    EventBus.subscribe('before:insales:cart', (eventData) => {
      if (eventData.action.method === 'delete_items') {
        this.saveOrder();
      }
    });

    EventBus.subscribe('remove_items:insales:cart', (cartOrder) => {
      if (!cartOrder.action.currentItems.length) { return; }

      const product = cartOrder.action.currentItems[0].product;
      this.send(this.getRemovedProduct(product, 1), 'remove_from_cart');
    });

    EventBus.subscribe('delete_items:insales:cart', (eventData) => {
      this.send(this.getDeletedProducts(eventData), 'remove_from_cart');
    });
  }

  addToFavoritesProductsEvent() {
    EventBus.subscribe('add_item:insales:favorites_products', (eventData) => {
      this.send(this.getFavoritedProducts(eventData), 'add_to_wishlist');
    });
  }

  changeNumberOfProductInCartEvent() {
    EventBus.subscribe('change_quantity:insales:item', (product) => {
      const lastButton = product.action.quantityState.lastButton;

      if (!lastButton) { return; }

      const changeQuantity = lastButton[0].getAttribute('data-quantity-change');
      const removeFromCartEnabled = this.counterSettings.remove_from_cart_event;
      const addToCartEnabled = this.counterSettings.add_to_cart_event;

      if (lastButton && changeQuantity == '-1' && removeFromCartEnabled) {
        this.send(this.getChangedProductFromCart(product, 1), 'remove_from_cart');
      } else if (lastButton && changeQuantity == '1' && addToCartEnabled) {
        this.send(this.getChangedProductFromCart(product, 1), 'add_to_cart');
      } else if (addToCartEnabled) {
        this.send(this.getChangedProductFromCart(product, product.action.quantity.current), 'add_to_cart');
      }
    });
  }

  quickCheckoutSuccessEvent() {
    EventBus.subscribe('success:insales:quick_checkout', (eventData) => {
      if (eventData.cart && eventData.task) {
        this.sendPurchaseEvent(eventData);
      }
    });
  }

  send(sendData, method) {
    const eventData = {
      event: method,
      ecommerce: {
        value: Number(this.calculateTotalValue(sendData)),
        currency: this.ecommerce.ecommerce.currencyCode,
        items: sendData,
      },
    };
    this.layer.push(eventData);

    // Дополнительный push для SberAds если он настроен И событие включено
    if (this.sberAdsDataLayerName && this.isSberAdsEventEnabled(method)) {
      this.sendSberAdsEvent(method, sendData);
    }
  }

  isSberAdsEventEnabled(method) {
    switch (method) {
      case 'add_to_cart':
        return this.counterSettings.add_to_cart_event;
      case 'remove_from_cart':
        return this.counterSettings.remove_from_cart_event;
      case 'purchase':
        return this.counterSettings.purchase_event;
      default:
        return false;
    }
  }

  sendSberAdsEvent(method, sendData) {
    // Маппинг событий GA4 -> SberAds
    const sberAdsMethodMap = {
      'add_to_cart': 'add',
      'remove_from_cart': 'remove',
      'purchase': 'purchase'
    };

    const sberAdsMethod = sberAdsMethodMap[method];
    if (!sberAdsMethod) {
      return;
    }

    const sberAdsData = {
      ecommerce: {
        currencyCode: this.ecommerce.ecommerce.currencyCode,
        [sberAdsMethod]: {
          products: sendData.map(item => ({
            id: String(item.item_id || item.id),
            name: String(item.item_name || item.name || 'Товар'),
            price: Number(item.price || 0),
            quantity: Number(item.quantity || 1),
            brand: item.item_brand || item.brand || '',
            category: item.item_category || item.category || '',
            variant: item.item_variant || item.variant || ''
          }))
        }
      }
    };

    if (sberAdsMethod === 'purchase' && this.lastPurchaseOrderId) {
      sberAdsData.ecommerce[sberAdsMethod].actionField = {
        id: this.lastPurchaseOrderId
      };
    }

    window[this.sberAdsDataLayerName].push(sberAdsData);
  }

  sendPurchaseEvent(eventData) {
    const { task, cart } = eventData;
    let items = [];

    if (task.withoutCart) {
      // В форме есть поле fakeOrder
      if (task.fakeOrder) {
        const item_name = task.fakeOrderName || 'Форма';

        items = [{
          item_id: null,
          item_name,
          price: 0,
          quantity: 1
        }];
      // Заказ оформленный с независимой корзиной (items прилетает с бэка)
      } else {
        items = task.items;
      }
    // Заказ оформленный с корзиной
    } else {
      items = items.concat(this.getPurchasedProducts(cart.order.order_lines));
    }

    this.lastPurchaseOrderId = task.order_number;

    const purchaseData = {
      event: 'purchase',
      ecommerce: {
        transaction_id: task.order_number,
        value: cart.order.total_price,
        currency: cart.tasks._currency_code === 'RUR' ? 'RUB' : cart.tasks._currency_code,
        items
      }
    };

    this.layer.push(purchaseData);

    // Дополнительный push для SberAds если он настроен И событие включено
    if (this.sberAdsDataLayerName && this.isSberAdsEventEnabled('purchase')) {
      this.sendSberAdsEvent('purchase', items);
    }
  }

  getPurchasedProducts(orderLines) {
    return orderLines.map(line => ({
      item_id: String(line.id),
      item_name: line.title,
      price: Number(line.full_total_price),
      quantity: Number(line.quantity),
    }));
  }

  calculateTotalValue(items) {
    return items.reduce((total, item) => total + item.price * item.quantity, 0);
  }

  getAddedProducts(products) {
    const addedItems = products.action.items;

    // Учитываем, что при вызове формы заказа в один клик или формы заявки она может быть fake_order (без товаров)
    if (!addedItems) { return []; }

    const productsArray = [];

    const clonedLines = cloneDeep(products.order_lines);
    const keyedLines = {};
    for (const line of clonedLines) {
      // Если в корзине есть варианты товаров с опциями, то нужно сгруппировать позиции с одинаковыми ID и разными значениями опций в один массив
      if (line.accessory_value_ids) {
        if (!keyedLines[line.id]) {
          keyedLines[line.id] = [ line ];
        } else if (keyedLines[line.id] && !keyedLines[line.id].length) {
          keyedLines[line.id] = [ keyedLines[line.id], line ];
        } else if (keyedLines[line.id] && keyedLines[line.id].length) {
          keyedLines[line.id].push(line);
        }
      } else {
        keyedLines[line.id] = line;
      }
    }

    const pickedLines = {};
    for (const key of Object.keys(addedItems)) {
      if (key !== 'accessoriable_variant_ids') {
        pickedLines[key] = keyedLines[key];
      } else {
        // Если в корзину был добавлен товар с выбранными опциями, то нужно найти в корзине вариант товара с этими опциями
        Object.keys(addedItems[key]).forEach(accessoriableItemId => {
          const accessoriableItems = addedItems[key][accessoriableItemId];
          const accessoriableOrderLines = keyedLines[accessoriableItemId];

          if (!accessoriableOrderLines || !accessoriableOrderLines.length) { return; }

          accessoriableItems.forEach(line => {
            accessoriableOrderLines.forEach(orderLine => {
              if (haveSameKeys(line.accessory_value_ids, orderLine.accessory_value_ids)) {
                pickedLines.accessoriable_variant_ids = [] || pickedLines.accessoriable_variant_ids;

                pickedLines.accessoriable_variant_ids.push(orderLine);
              }
            });
          });
        });
      }
    }

    for (const [ key, line ] of Object.entries(pickedLines)) {
      // Если это массив вариантов с аксессуарами
      if (key === 'accessoriable_variant_ids' && line) {
        line.forEach(accessoriableVariant => {
          productsArray.push(this.addedProduct(accessoriableVariant, addedItems));
        });

        continue;
      // Если это массив вариантов с аксессуарами, но при добавлении в корзину опции выбраны не были. Например, если сначала добавили товар с опциями, а потом решили добавить без опций
      } else if (line && line.length) {
        productsArray.push(this.addedProduct(line.find(itemLine => !itemLine.accessory_value_ids), addedItems));

        continue;
      }
      // Если это обычный товар
      else if (line) {
        productsArray.push(this.addedProduct(line, addedItems));
      } else {
        console.warn('line undefined for', key);
      }
    }

    return productsArray;
  }

  getVariantTitle(line) {
    let title = null;

    if (line.title !== line.product.title) {
      const titleParts = line.title.split('(');
      const [ tailPart ] = titleParts.slice(1);
      const splitParts = tailPart.split(')');
      [ title ] = splitParts;
    }

    return title;
  }

  addedProduct(line, addedItems) {
    let quantity = addedItems[line.id];

    // Не забываем прокинуть количество для товара с опциями
    if (addedItems.accessoriable_variant_ids) {
      quantity = line.quantity;
    }

    const variantTitle = this.getVariantTitle(line);
    const product = {
      item_id: String(line.product.id),
      item_name: String(line.product.title),
      quantity: Number(quantity),
      price: Number(line.sale_price),
    };

    if (variantTitle) {
      product.variant = String(variantTitle);
    }

    return product;
  }

  saveOrder() {
    this.savedOrderLines = cloneDeep(InsalesCommonCart.order.get().order_lines);
  }

  getRemovedProduct(product, count) {
    const removedProduct = {
      item_id: product.id,
      item_name: product.title,
      quantity: Number(count),
      price: Number(product.price),
    };

    return [ removedProduct ];
  }

  getDeletedProducts(eventData) {
    const deletedItems = eventData.action.items;
    const productsArray = [];

    for (const line of this.savedOrderLines) {
      if (
        (_isArray(deletedItems) && deletedItems.includes(line.id))
        || deletedItems[line.id]
      ) {
        productsArray.push(this.deletedProduct(line));
      }

      if (deletedItems.accessoriable_variant_ids && deletedItems.accessoriable_variant_ids[line.id]) {
        deletedItems.accessoriable_variant_ids[line.id].forEach(accessoriableVariant => {
          if (haveSameKeys(accessoriableVariant.accessory_value_ids, line.accessory_value_ids)) {
            productsArray.push(this.deletedProduct(line));
          }
        });
      }
    }

    return productsArray;
  }

  getChangedProductFromCart(product, count) {
    const changedProduct = {
      item_id: product.id,
      item_name: String(product.action.productJSON.title),
      quantity: Number(count),
      price: Number(product.price)
    };

    return [ changedProduct ];
  }

  deletedProduct(line) {
    const variantTitle = this.getVariantTitle(line);
    const product = {
      item_id: String(line.product.id),
      item_name: String(line.product.title),
      quantity: Number(line.quantity),
      price: Number(line.sale_price),
    };

    if (variantTitle) {
      product.variant = String(variantTitle);
    }

    if (line.accessory_value_ids) {
      product.accessory_value_ids = line.accessory_value_ids;
    }

    return product;
  }

  getFavoritedProducts(eventData) {
    const { products, action } = eventData;
    const favoritedProducts = [];

    if (products.length > 0) {
      const lastProduct = products[products.length - 1];
      favoritedProducts.push({
        item_id: String(lastProduct.id),
        item_name: String(lastProduct.title),
        quantity: 1,
        price: Number(lastProduct.price_min)
      });
    }

    return favoritedProducts;
  }
}

export default DataLayer;
